import { NotifierProvider } from '@kluein/klue-ui';
import { Notifications } from '@mantine/notifications';
import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';

import { AppContextProvider } from 'contexts';
import ErrorBoundary from 'lib/ErrorBoundary';

import App from './App';
import i18nInit from './i18n';
import { enableMocking } from './mock-server';
import store from './store';

const DevToolsRoot =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEV_INSPECTOR === 'true'
    ? lazy(() => import('dev-tools/DevToolsRoot'))
    : null;

/* Global styles */
import '@kluein/design-tokens/dist/index.css';
import '@kluein/klue-ui/dist/style.css';
import '@mantine/core/styles.layer.css';
import '@mantine/carousel/styles.layer.css';
import '@mantine/dates/styles.layer.css';
import '@mantine/notifications/styles.layer.css';
import '@kluein/nectar/dist/nectar.css';

/* The design-system floor: design/tokens.yaml -> design/tokens.css.
   After the vendor sheets on purpose — where a name is defined twice at
   :root, the later declaration wins, and the floor should be the winner. */
import '../design/tokens.css';

import 'components/common/klueCard/card-static-html-content.css';
import 'components/common/klueCard/card-static-html-dynamic-blocks.css';
import 'components/common/klueCard/card-static-html-overrides.css';
import 'components/vendor/pendo/pendo.css';

/* The Hatch shell's display serif. Provisioned here rather than in the token
   floor because editorial artifacts don't share it — see the file's header. */
import 'modules/hatch/theme/hatchFonts.css';

i18nInit();

const container = document.getElementById('root');
const root = createRoot(container as HTMLElement);

enableMocking()
  .catch()
  .finally(() => {
    root.render(
      <Suspense fallback={<div />}>
        <ErrorBoundary fullscreen={true}>
          <Provider store={store}>
            <BrowserRouter>
              <QueryParamProvider adapter={ReactRouter6Adapter}>
                <AppContextProvider>
                  <NotifierProvider>
                    <Notifications position="top-right" />
                    <App />
                    {DevToolsRoot && (
                      <Suspense fallback={null}>
                        <DevToolsRoot />
                      </Suspense>
                    )}
                  </NotifierProvider>
                </AppContextProvider>
              </QueryParamProvider>
            </BrowserRouter>
          </Provider>
        </ErrorBoundary>
      </Suspense>,
    );
  });
